<script>
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';

export default {
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      currentIntent: null,
      key: 0,
      pollingTimer: null,
      pollingCount: 0,
      maxPolls: 30, // Increased - poll for up to 60 seconds
      pollingInterval: 2000, // Every 2 seconds
      isPolling: false,
      debugMode: true, // Enable for debugging info
      lastFetchedData: null, // Store last response for debugging
    };
  },
  computed: {
    formattedIntentName() {
      if (!this.intentToDisplay || !this.intentToDisplay.name) {
        return '';
      }

      return this.intentToDisplay.name;
    },

    intentToDisplay() {
      return this.currentIntent;
    },

    conversationIdNumber() {
      return parseInt(this.conversationId, 10);
    },
  },
  mounted() {
    // Initial fetch
    this.fetchIntentDirectly();

    // Setup all listeners
    this.setupListeners();
    this.setupPollingListeners();

    // Add a fallback refresh after a delay - this is a last resort
    setTimeout(() => {
      if (!this.currentIntent) {
        this.fetchIntentDirectly();
      }
    }, 10000);
  },
  beforeUnmount() {
    // Clean up event listeners
    emitter.off('conversation.status_changed');

    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }

    if (this.cleanupListener) {
      this.cleanupListener();
    }
  },
  methods: {
    setupListeners() {
      // Subscribe to Vuex store mutations
      this.$store.subscribe((mutation, state) => {
        if (mutation.type === 'conversations/updateConversation') {
          const updatedConversation = mutation.payload;
          if (updatedConversation.id === this.conversationIdNumber) {
            this.fetchIntentDirectly();
          }
        }
      });

      // Listen directly to events via mitt
      this.directListener = event => {
        if (event.conversationId === this.conversationIdNumber) {
          this.fetchIntentDirectly();
        }
      };
      emitter.on('conversation.status_changed', this.directListener);

      // Also use the composable API
      this.cleanupListener = useEmitter(
        'conversation.status_changed',
        event => {
          if (event.conversationId === this.conversationIdNumber) {
            this.fetchIntentDirectly();
          }
        }
      );
    },

    setupPollingListeners() {
      // Watch for conversation resolved events
      emitter.on('conversation.status_changed', event => {
        if (
          event.conversationId === this.conversationIdNumber &&
          event.status === 'resolved'
        ) {
          this.startPollingForIntent();
        }
      });

      this.$store.subscribe((mutation, state) => {
        if (mutation.type === 'conversations/updateConversation') {
          const updatedConversation = mutation.payload;
          if (
            updatedConversation.id === this.conversationIdNumber &&
            updatedConversation.status === 'resolved'
          ) {
            this.startPollingForIntent();
          }
        }
      });
    },

    startPollingForIntent() {
      // Don't duplicate polling efforts
      if (this.isPolling) {
        return;
      }

      // Clean up existing timer
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
      }

      this.isPolling = true;
      this.pollingCount = 0;

      // Start polling with more aggressive approach
      this.pollingTimer = setInterval(() => {
        this.pollingCount++;

        // Always force a fresh API call by adding a cache-busting parameter
        const cacheBuster = new Date().getTime();
        this.fetchIntentDirectly(cacheBuster).then(hasIntent => {
          // Only stop if we definitely found an intent or hit max attempts
          if (hasIntent || this.pollingCount >= this.maxPolls) {
            clearInterval(this.pollingTimer);
            this.pollingTimer = null;
            this.isPolling = false;

            // If we hit max polls with no intent, try one final refresh
            if (!hasIntent) {
              setTimeout(() => {
                this.fetchIntentDirectly(true); // True = final attempt
              }, 5000);
            }
          }
        });
      }, this.pollingInterval);
    },

    fetchIntentDirectly(cacheBuster) {
      this.loading = true;
      const isFinalAttempt = cacheBuster === true;

      const accountIdMatch =
        window.location.pathname.match(/\/accounts\/(\d+)/);
      const accountId = accountIdMatch ? accountIdMatch[1] : null;

      if (!accountId || !this.conversationId) {
        this.loading = false;
        return Promise.resolve(false);
      }

      // Build URL with cache buster if needed
      let url = `/api/v1/accounts/${accountId}/conversations/${this.conversationId}`;
      if (cacheBuster && typeof cacheBuster === 'number') {
        url += `?_=${cacheBuster}`;
      }

      return window.axios
        .get(url)
        .then(response => {
          const conversation = response.data;
          this.lastFetchedData = conversation; // Store for debugging

          let foundIntent = null;
          let intentSource = '';

          // Check all possible places for intent
          if (conversation.intent) {
            foundIntent = conversation.intent;
            intentSource = 'direct';
          } else if (conversation.meta?.intent) {
            foundIntent = conversation.meta.intent;
            intentSource = 'meta';
          } else if (conversation.additional_attributes?.intent) {
            foundIntent = conversation.additional_attributes.intent;
            intentSource = 'additional_attributes';
          } else if (conversation.custom_attributes?.intent) {
            foundIntent = conversation.custom_attributes.intent;
            intentSource = 'custom_attributes';
          }

          // If we found intent
          if (foundIntent) {
            // Normalize to object format
            const newIntent =
              typeof foundIntent === 'object'
                ? foundIntent
                : { name: foundIntent };

            // Update if different from current
            if (
              !this.currentIntent ||
              this.currentIntent.name !== newIntent.name
            ) {
              this.currentIntent = newIntent;
              this.key++; // Force re-render
            }

            this.loading = false;
            return true; // Intent found
          }

          this.loading = false;
          return false; // No intent found
        })
        .catch(error => {
          this.error = error.message;
          this.loading = false;
          return false;
        });
    },
  },
};
</script>

<template>
  <div class="conversation-intent" :key="key">
    <div v-if="intentToDisplay" class="intent-details">
      <h4 class="intent-name">
        {{ intentToDisplay.name }}
      </h4>
    </div>
    <div v-else class="flex items-center justify-center py-2 text-sm">
      No intent detected
    </div>

    <!-- Error display -->
    <div v-if="error" class="text-sm text-red-400 dark:text-red-500 mt-1">
      {{ error }}
    </div>
  </div>
</template>

<style scoped>
.intent-name {
  text-transform: none !important;
}
</style>